/**
 * React hook that walks the attached folder once and caches a flat file
 * list for the chat composer's `@` mention picker. Re-walks when the
 * directory handle identity changes (e.g. user attaches a different folder).
 *
 * @module file-agent/lib/use-file-index
 */

import { useEffect, useRef, useState } from "react";
import { buildFileIndex, type FileIndex } from "./file-index";

interface UseFileIndexResult {
  index: FileIndex | null;
  loading: boolean;
  /** Force a re-walk — call after the user adds/renames files mid-session. */
  refresh: () => void;
}

/**
 * Build and cache a file index for the given handle. Returns null until
 * the first walk completes; subsequent walks update the cache in place.
 */
export function useFileIndex(
  handle: FileSystemDirectoryHandle | null,
): UseFileIndexResult {
  const [index, setIndex] = useState<FileIndex | null>(null);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0);
  const lastHandleRef = useRef<FileSystemDirectoryHandle | null>(null);

  useEffect(() => {
    if (!handle) {
      setIndex(null);
      lastHandleRef.current = null;
      return;
    }

    let cancelled = false;
    setLoading(true);
    lastHandleRef.current = handle;

    buildFileIndex(handle)
      .then((next) => {
        if (cancelled) return;
        setIndex(next);
      })
      .catch(() => {
        if (cancelled) return;
        setIndex({ files: [], truncated: false });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [handle, version]);

  return {
    index,
    loading,
    refresh: () => setVersion((v) => v + 1),
  };
}
